import { parseServerTimestamp } from '@/utils/time'
import { POLL_STAYS_UP_SECONDS } from '../limits'
import type { RoomActivePoll } from '../types'

/**
 * How long a poll stays up (ORB-1088): the choices the Room tab and the moderator's new-poll form offer, and the poll
 * showing as of now. A poll with a stay closes by itself and nothing is broadcast then, so every reader of the room's
 * active poll filters it here, when it reads: the room state is replaced whole on a rejoin or a reload, and an expiry
 * applied to the store once would come back with it.
 *
 * The close is timed on this page's clock from the server's `SecondsLeft`, not by comparing `ClosesUtc` with this
 * page's clock: a viewer's clock 30 s out would otherwise close the poll 30 s early or late. The time the state took to
 * arrive is the only error left.
 */

/** A new poll, on the Room tab or written live, stays up a minute unless someone picks otherwise. */
export const DEFAULT_POLL_STAYS_UP_SECONDS = 60

/** Every choice in the order it is offered; null is "Until the next poll". */
export const POLL_STAY_CHOICES: readonly (number | null)[] = [null, ...POLL_STAYS_UP_SECONDS]

/** "30 seconds", "1 minute", "2 minutes". */
export function stayText(seconds: number): string {
    if (seconds % 60 !== 0) return `${seconds} second${seconds === 1 ? '' : 's'}`
    const minutes = seconds / 60
    return `${minutes} minute${minutes === 1 ? '' : 's'}`
}

/** The choice as a menu names it. */
export function stayLabel(seconds: number | null): string {
    return seconds === null ? 'Until the next poll' : stayText(seconds)
}

/** A choice as a select's value: "" for until the next poll, a real option, so the select shows no placeholder. */
export function stayValue(seconds: number | null): string {
    return seconds === null ? '' : String(seconds)
}

export function stayFromValue(value: string): number | null {
    return value === '' ? null : Number(value)
}

type PollDeadline = Pick<RoomActivePoll, 'ClosesUtc' | 'closesAtMs'>

/** The poll as it arrived at `receivedMs` on this page's clock, with its close on that clock too. */
export function withClientDeadline<T extends RoomActivePoll>(poll: T, receivedMs: number): T {
    return poll.SecondsLeft == null ? poll : { ...poll, closesAtMs: receivedMs + poll.SecondsLeft * 1000 }
}

/** Seconds until the poll closes by itself, or null for one that stays up until the next poll. */
export function pollSecondsLeft(poll: PollDeadline, nowMs: number): number | null {
    // ClosesUtc only for a poll that arrived without the server's count, which an API from before it never sends.
    const closesMs = poll.closesAtMs ?? parseServerTimestamp(poll.ClosesUtc)
    return closesMs === null ? null : (closesMs - nowMs) / 1000
}

/** The poll showing at `nowMs`: the room's active poll, unless it has closed by itself. */
export function openPoll<T extends PollDeadline>(poll: T | null | undefined, nowMs: number): T | null {
    if (!poll) return null
    const secondsLeft = pollSecondsLeft(poll, nowMs)
    return secondsLeft !== null && secondsLeft <= 0 ? null : poll
}
